// The review version every save is based on (ADR-0009, decision 4). The adapter stamps the
// version it knows onto the engine's write commands; the API answers 409 when it moved, and the
// answer to an accepted save carries the new one. The registry lives beside the adapter, not in
// React state, because the adapter is a plain function the ported screen calls.
import type { ReviewLease } from './review-leases';

/** A save refused by the fence: somebody else holds the document, or it changed under the reviewer. */
export class ReviewConflictError extends Error {
  constructor(
    message: string,
    public readonly code: 'review_held' | 'review_stale',
    public readonly lease: ReviewLease,
  ) {
    super(message);
    this.name = 'ReviewConflictError';
  }
}

const versions = new Map<string, number>();
const listeners = new Set<(conflict: ReviewConflictError) => void>();

/** The version a document's next save is based on; the server's when a lease was granted, else unknown. */
export function reviewVersionOf(documentId: string): number | undefined {
  return versions.get(documentId);
}

export function rememberReviewVersion(documentId: string, version: number): void {
  versions.set(documentId, version);
}

/** Tells the screen a save was refused, so it can show the latest; returns the unsubscribe. */
export function onReviewConflict(listener: (conflict: ReviewConflictError) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

type Args = Record<string, unknown>;

/** A non-empty string, or null. */
const id = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

/** The `id` of an object-shaped argument, or null. */
const of = (value: unknown): string | null => id((value as { id?: unknown } | undefined)?.id);

/** A nested argument, without the casts at every call. */
const at = (args: Args, key: string, field: string): unknown =>
  (args[key] as Record<string, unknown> | undefined)?.[field];

/**
 * Where each write command names the document its version is measured against, as the API reads it
 * (`ReviewWrites` in services/Paige.Api/Engine/ReviewWrite.cs). Keep the two in step: a command the
 * API fences and this map does not know sends no version, so the fence can only refuse it on the
 * lease, not on a stale read.
 */
const writtenDocument: Readonly<Record<string, (args: Args) => string | null>> = {
  'save-document-correction': (args) => of(args['document']),
  'save-document-corrections': (args) =>
    of((args['correctionEntries'] as { document?: unknown }[] | undefined)?.[0]?.document),
  // A split or a merge replaces several documents; the first is the one the workbench counts from
  // and the API bumps all of them together.
  'save-boundary-mutation': (args) =>
    id((args['removedDocumentIds'] as unknown[] | undefined)?.[0]),
  'withdraw-row-confirmation': (args) =>
    of(at(args, 'rowConfirmationWithdrawal', 'expectedDocument')),
  'withdraw-type-confirmation': (args) =>
    of(at(args, 'typeConfirmationWithdrawal', 'expectedDocument')),
  'withdraw-field-confirmation': (args) => id(args['documentId']),
  // Ctrl+Z after a value was saved (CapturePoint ef0ff40e): the field goes back as it was.
  'undo-field-edit': (args) => id(args['documentId']),
  'remove-page': (args) => id(args['documentId']),
  'restore-page': (args) => id(args['documentId']),
  'turn-page': (args) => id(args['documentId']),
  // Delete and Restore name several documents; the API bumps all of them together.
  'delete-documents': (args) => id((args['documentIds'] as unknown[] | undefined)?.[0]),
  'restore-documents': (args) => id((args['documentIds'] as unknown[] | undefined)?.[0]),
  // "Find the table" saves the rows it found onto the document.
  'find-document-table': (args) => id(args['documentId']),
};

/** The document a write command's version is measured against; null for anything else. */
export function writtenDocumentOf(command: string, args: Args): string | null {
  return Object.hasOwn(writtenDocument, command)
    ? (writtenDocument[command] as (a: Args) => string | null)(args)
    : null;
}

/** The command's arguments with the version the save is based on, when one is known. */
export function withReviewVersion(
  command: string,
  args: Record<string, unknown>,
): Record<string, unknown> {
  const documentId = writtenDocumentOf(command, args);
  const version = documentId ? versions.get(documentId) : undefined;
  return version === undefined ? args : { ...args, reviewVersion: version };
}

/** An accepted save's answer carries the new version; a refused one is raised for the screen. */
export function readReviewAnswer(
  command: string,
  args: Record<string, unknown>,
  status: number,
  body: {
    reviewVersion?: unknown;
    error?: { code?: string; message?: string };
    lease?: ReviewLease;
  },
): void {
  const documentId = writtenDocumentOf(command, args);
  if (!documentId) return;
  if (typeof body.reviewVersion === 'number') versions.set(documentId, body.reviewVersion);
  if (status !== 409 || !body.lease) return;
  const code = body.error?.code === 'review_held' ? 'review_held' : 'review_stale';
  versions.set(documentId, body.lease.reviewVersion);
  const conflict = new ReviewConflictError(
    body.error?.message ?? 'This document changed; here is the latest',
    code,
    body.lease,
  );
  for (const listener of listeners) listener(conflict);
  throw conflict;
}
